// Story 1.24 — approved-creator surface for browsing currently-
// recruiting portfolios + applying with an optional drop-specific
// hello-text override. The collapsible "My applications" section
// beneath the browse list is the poor-man's dashboard tab until
// Story 1.26 ships /account/creator-dashboard.

import { useState } from "react";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  Textarea,
} from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";

import {
  isFormerCreator,
  useAccountMe,
} from "../features/admin/useAccountMe";
import {
  useMyPortfolioApplications,
  type MyApplicationsStatusFilter,
  type MyPortfolioApplication,
} from "../features/creator-portfolios/useMyPortfolioApplications";
import {
  useMyPortfolioMemberships,
  type PortfolioMembership,
} from "../features/creator-portfolios/useMyPortfolioMemberships";
import { useWithdrawPortfolioConsent } from "../features/creator-portfolios/useWithdrawPortfolioConsent";
import {
  useRecruitingPortfolios,
  type RecruitingPortfolio,
} from "../features/creator-portfolios/useRecruitingPortfolios";
import { useSubmitPortfolioApplication } from "../features/creator-portfolios/useSubmitPortfolioApplication";
import { useWithdrawPortfolioApplication } from "../features/creator-portfolios/useWithdrawPortfolioApplication";
import { statusBadgeVariant } from "../lib/portfolio-status-badge";

const HELLO_TEXT_OVERRIDE_MAX = 140;

function formatDeadline(iso: string | null): string {
  if (!iso) return "No deadline";
  const d = new Date(iso);
  return `Closes ${d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })}`;
}

export function CreatorPortfoliosPage() {
  const session = useAccountMe();
  const former = isFormerCreator(session.data?.data);

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
            Recruiting portfolios
          </h1>
          <p className="opacity-80">
            Apply to be included in upcoming dossier compositions. Approved
            applications show up on the admin's composer; admin picks the
            final roster.
          </p>
        </header>

        {former && (
          <div className="mb-8 rounded-md border border-brand-forest-300 bg-brand-paper-50 p-4 text-sm">
            <p className="font-semibold mb-1">
              You've withdrawn from the creator program.
            </p>
            <p className="opacity-80">
              Your history stays visible here, and you can still withdraw
              consent from portfolios you remain on. Applying to new
              portfolios requires becoming an approved creator again — you
              can re-apply from the become-a-creator page.
            </p>
          </div>
        )}

        <RecruitingList canApply={!former} />

        <section className="mt-12">
          <h2 className="text-xl font-heading font-bold mb-4">
            My portfolios
          </h2>
          <MyMembershipsPanel />
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-heading font-bold mb-4">
            My applications
          </h2>
          <MyApplicationsPanel />
        </section>
      </div>
    </main>
  );
}

// =============================================================================
// Browse list
// =============================================================================

function RecruitingList({ canApply }: { canApply: boolean }) {
  const query = useRecruitingPortfolios(1);

  if (query.isPending) {
    return (
      <div className="grid gap-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-32 w-full rounded-lg" />
        ))}
      </div>
    );
  }
  if (query.isError) {
    return (
      <p className="text-destructive">
        Couldn't load recruiting portfolios. Try refreshing.
      </p>
    );
  }
  const items = query.data?.data ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        aria-label="No recruiting portfolios"
        description="No portfolios are currently recruiting. Check back as the admin composes new drops."
      />
    );
  }

  return (
    <div className="grid gap-4">
      {items.map((p) => (
        <RecruitingPortfolioCard key={p.id} portfolio={p} canApply={canApply} />
      ))}
    </div>
  );
}

function RecruitingPortfolioCard({
  portfolio,
  canApply,
}: {
  portfolio: RecruitingPortfolio;
  canApply: boolean;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [helloOverride, setHelloOverride] = useState("");
  const submit = useSubmitPortfolioApplication();

  const handleConfirm = async () => {
    try {
      await submit.mutateAsync({
        portfolio_id: portfolio.id,
        optional_drop_specific_hello_text:
          helloOverride.trim().length > 0 ? helloOverride : null,
      });
      toast.success(
        `Application submitted for ${portfolio.title}. Admin reviews within 48 hours.`,
      );
      setDialogOpen(false);
      setHelloOverride("");
    } catch {
      // useSubmitPortfolioApplication's onError already toasted; keep
      // dialog open so the user can adjust + retry.
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>{portfolio.title}</CardTitle>
            {portfolio.theme && (
              <CardDescription>{portfolio.theme}</CardDescription>
            )}
          </div>
          {portfolio.already_applied ? (
            <Button variant="secondary" disabled>
              {portfolio.my_application_status === "approved"
                ? "Approved — you're in"
                : "Application pending"}
            </Button>
          ) : !canApply ? (
            <Button
              variant="secondary"
              disabled
              title="Applying requires approved creator status."
            >
              Creator status required
            </Button>
          ) : (
            <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <Button
                variant="primary"
                onClick={() => setDialogOpen(true)}
                aria-label={`Apply to ${portfolio.title}`}
              >
                Apply
              </Button>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Apply to {portfolio.title}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {portfolio.theme
                      ? `${portfolio.theme}. `
                      : ""}
                    Your profile hello-text will appear on every share
                    certificate from this drop unless you override it below.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="my-4 space-y-2">
                  <label
                    htmlFor="hello-override"
                    className="block text-sm font-medium"
                  >
                    Optional drop-specific hello text
                  </label>
                  <Textarea
                    id="hello-override"
                    value={helloOverride}
                    onChange={(e) => setHelloOverride(e.target.value)}
                    maxCharacters={HELLO_TEXT_OVERRIDE_MAX}
                    rows={3}
                    placeholder="Leave blank to use your profile hello-text."
                    aria-label="Optional drop-specific hello text override"
                  />
                  <p className="text-xs opacity-70">
                    Overrides your profile hello-text for THIS portfolio's
                    certificates only.
                  </p>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setHelloOverride("")}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleConfirm}
                    disabled={submit.isPending}
                  >
                    {submit.isPending ? "Submitting…" : "Submit application"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {portfolio.target_creator_count !== null && (
            <>
              <dt className="opacity-70">Target creators</dt>
              <dd>{portfolio.target_creator_count}</dd>
            </>
          )}
          <dt className="opacity-70">Recruiting window</dt>
          <dd>{formatDeadline(portfolio.recruiting_deadline)}</dd>
        </dl>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// My portfolios (roster memberships) panel
// =============================================================================

function membershipStatusLabel(m: PortfolioMembership): string {
  if (m.withdrawn_at) return "withdrawn";
  if (m.recruiting_status === "stalled") return "stalled";
  if (m.drop_status) return `drop ${m.drop_status}`;
  return m.recruiting_status;
}

function MyMembershipsPanel() {
  const query = useMyPortfolioMemberships();

  if (query.isPending) {
    return <Skeleton className="h-24 w-full rounded-lg" />;
  }
  if (query.isError) {
    return <p className="text-destructive">Couldn't load your portfolios.</p>;
  }
  const items = query.data ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        aria-label="No portfolio memberships"
        description="You're not on any portfolio roster yet. Apply to a recruiting portfolio above — or an admin may add you directly."
      />
    );
  }
  return (
    <div className="grid gap-3">
      {items.map((m) => (
        <MembershipRow key={m.portfolio_id} membership={m} />
      ))}
    </div>
  );
}

function MembershipRow({ membership }: { membership: PortfolioMembership }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const withdraw = useWithdrawPortfolioConsent();
  const withdrawn = membership.withdrawn_at !== null;

  const handleConfirm = async () => {
    try {
      const outcome = await withdraw.mutateAsync({
        portfolio_id: membership.portfolio_id,
      });
      toast.success(
        outcome.creator_status_revoked
          ? `Consent withdrawn from ${membership.title}. Your creator status has been revoked.`
          : `Consent withdrawn from ${membership.title}.`,
      );
      setDialogOpen(false);
    } catch {
      // useWithdrawPortfolioConsent.onError already toasted; keep the
      // dialog open so the user can read the toast.
    }
  };

  return (
    <Card className={withdrawn ? "opacity-60" : undefined}>
      <CardContent className="py-4 flex items-center justify-between gap-4">
        <div>
          <p className="font-medium">{membership.title}</p>
          {membership.theme && (
            <p className="text-sm opacity-70">{membership.theme}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={withdrawn ? "outline" : "secondary"}>
            {membershipStatusLabel(membership)}
          </Badge>
          {!withdrawn && (
            <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setDialogOpen(true)}
                aria-label={`Withdraw consent from ${membership.title}`}
              >
                Withdraw consent
              </Button>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Withdraw consent from {membership.title}?
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-2">
                      <p className="font-semibold text-destructive">
                        This will revoke your creator status. You leave the
                        creator program and stop being featured in future
                        sales of this dossier.
                      </p>
                      <p>
                        Already-sold dossiers are NOT affected: certificates
                        and sealed dossiers bought before your withdrawal can
                        still include your profile as usual. Your withdrawal
                        only applies to future dossier purchases.
                      </p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep consent</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleConfirm}
                    disabled={withdraw.isPending}
                  >
                    {withdraw.isPending
                      ? "Withdrawing…"
                      : "Withdraw & revoke my creator status"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// My applications panel
// =============================================================================

function MyApplicationsPanel() {
  const [statusFilter, setStatusFilter] =
    useState<MyApplicationsStatusFilter>("any");
  const query = useMyPortfolioApplications(statusFilter, 1);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(["any", "pending", "approved", "rejected", "withdrawn"] as const).map(
          (s) => (
            <Button
              key={s}
              variant={statusFilter === s ? "primary" : "tertiary"}
              size="sm"
              onClick={() => setStatusFilter(s)}
              aria-pressed={statusFilter === s}
            >
              {s === "any" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ),
        )}
      </div>
      {query.isPending ? (
        <Skeleton className="h-24 w-full rounded-lg" />
      ) : query.isError ? (
        <p className="text-destructive">Couldn't load your applications.</p>
      ) : (query.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          aria-label="No portfolio applications"
          description="No portfolio applications yet."
        />
      ) : (
        <div className="grid gap-3">
          {(query.data?.data ?? []).map((app) => (
            <MyApplicationRow key={app.id} application={app} />
          ))}
        </div>
      )}
    </div>
  );
}

function MyApplicationRow({ application }: { application: MyPortfolioApplication }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const withdraw = useWithdrawPortfolioApplication();

  const isPending = application.status === "pending";

  const handleConfirm = async () => {
    try {
      await withdraw.mutateAsync({
        portfolio_application_id: application.id,
      });
      toast.success(`Withdrawn from ${application.portfolio_title}.`);
      setDialogOpen(false);
    } catch {
      // useWithdrawPortfolioApplication.onError already toasted; leave
      // the dialog open so the user can read the toast + try again.
    }
  };

  return (
    // Story 1.26 — `id` anchor enables the creator dashboard's
    // "Manage" link to deep-link directly to this row.
    <Card id={`application-${application.id}`}>
      <CardContent className="py-4 flex items-center justify-between gap-4">
        <div>
          <p className="font-medium">{application.portfolio_title}</p>
          {application.portfolio_theme && (
            <p className="text-sm opacity-70">{application.portfolio_theme}</p>
          )}
          <p className="text-xs opacity-60 mt-1">
            Submitted {new Date(application.submitted_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={statusBadgeVariant(application.status)}>
            {application.status}
          </Badge>
          {isPending && (
            <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setDialogOpen(true)}
                aria-label={`Withdraw application for ${application.portfolio_title}`}
              >
                Withdraw
              </Button>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Withdraw your application?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You can re-apply to {application.portfolio_title} as long as
                    the recruiting window stays open.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep application</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleConfirm}
                    disabled={withdraw.isPending}
                  >
                    {withdraw.isPending ? "Withdrawing…" : "Withdraw"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
